import _ from 'lodash';
import clsx from 'clsx';
import {
  makeStyles,
  Box,
  Icon,
  Button,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../../../../../common/hooks';
import { DateUtils, StringUtils } from '../../../../../common/utils';
import { DetailsSection, EmptyState, ListSection } from '../../../../../common/components';
import SubscriptionChange from './SubscriptionChange';
import SubscriptionDiscountForm from './SubscriptionDiscountForm';
import SubscriptionAddonAdmin from './SubscriptionAddonAdmin';
import { PRODUCT_NAMES_BY_ID } from '../../../constants';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(2),
  },
  content: {
    flex: '1',
    display: 'flex',
    padding: theme.spacing(2),
  },
  wrapper: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    marginRight: theme.spacing(4),
    '&:last-child': {
      marginRight: '0',
    },
  },
  section: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
  },
  productChangeSection: {
    marginTop: theme.spacing(3),
  },
  discountSection: {
    marginTop: theme.spacing(3),
  },
  eventsSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2),
    '& .MuiButton-root': {
      marginLeft: theme.spacing(1),
    },
    '& .MuiButton-root:first-child': {
      marginLeft: '0',
    },
  },
  back: {
    marginRight: 'auto',
  },
  checkbox: {
    marginTop: theme.spacing(1),
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '920px',
  },
}));
// SUOL: Should we consider moving these into a types file? This could be an improvement later :D
interface PaymentInfo {
  pspId?: number;
  pspCustomerId?: string;
  recurringId?: string;
  paymentType?: string;
  cardUpdate?: string;
  cardType?: string;
  cardCountry?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
  cardMask?: string;
  cardPrefix?: string;
  pic?: string;
  cardBlockedDate?: string;
  discountUnlockDate?: string;
}

interface ProductChange {
  id?: number;
  name?: string;
  duration?: number;
  price?: number;
  effective_date?: string;
  orderLines?: Array<{ value?: number }>;
}

interface Provider {
  id?: number;
  key?: string;
  name?: string;
  created?: string;
  last_modified?: string;
}

interface ExternalSubscriptionResponse {
  productId: number;
  status: string;
  statusChangedAt?: string;
  externalId: string;
  externalUserId: string;
}

interface Subscription {
  id?: number;
  type?: string;
  status?: string;
  startTime?: string;
  nextPaymentTime?: string;
  lastModified?: string;
  expireTime?: string;
  commitmentEndTime?: string;
  productName?: string;
  amount?: number;
  vatAmount?: number;
  duration?: number;
  providerId?: number;
  productId?: number;
  paymentCount?: number;
  subscriberBlocked?: boolean;
  discount?: {
    id?: number;
    discountType?: string;
    discountValue?: number;
    createdAt?: string;
    discountEndAt?: string;
  };
  events?: Array<{ created?: string; creator?: string; userEventType?: { displayText?: string; eventSubTypeDescription?: string; eventTypeDescription?: string } }>;
  products?: {
    id?: number;
    price?: number;
    productGroup?: string;
    title?: string;
    features?: { CHCHG?: Array<{ paramValue?: string }> };
    validDowngrades?: any[];
    validUpgrades?: any[];
  };
  productchange?: ProductChange;
  provider?: Provider;
  partnerExtensions?: { associateId?: number; vasToken?: string };
  paymentInfo?: PaymentInfo;
  pendingChanges?: Array<{ type?: string }>;
  externalSubscriptions?: {
    externalSubscriptionResponses?: ExternalSubscriptionResponse[];
  };
}

interface Product {
  id?: number;
  title?: string;
}

interface SubscriptionDetailsProps {
  subscription: Subscription;
  channels: any[];
  products: Product[];
  onResetCreditCard: () => void;
  onRemovePaymentBlock: (pic: string) => void;
  onRemoveUserBlock: () => void;
  onChangeSubscription: (value: any) => void;
  onCloseSubscription: (value: any) => void;
  onClosePartnerSubscription: (value: any) => void;
  onReactivateSubscription: () => void;
  onHideDetails: () => void;
  onAddSubscriptionDiscount: (value: any) => void;
  onFetchDiscountReasons: () => void;
  actionsDisabled: boolean;
  onResendAddonActivationLink: (productId: number) => void;
  onUnlinkAddonSubscription: (productId: number) => void;
}

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = ({
    channels,
    products,
    subscription,
    onResetCreditCard,
    onRemovePaymentBlock,
    onRemoveUserBlock,
    onChangeSubscription,
    onCloseSubscription,
    onClosePartnerSubscription,
    onReactivateSubscription,
    onHideDetails,
    onAddSubscriptionDiscount,
    onFetchDiscountReasons,
    actionsDisabled,
    onResendAddonActivationLink,
    onUnlinkAddonSubscription,
  }) => {
  const [t] = useTranslation();
  const { showDialog } = useDialog() as { showDialog: (config: any) => void };
  const classes = useStyles();

  const isPartnerAmbassador = subscription.providerId === 66;
  const isCardBlocked = subscription.paymentInfo && subscription.paymentInfo.cardBlockedDate;
  const isUserBlocked = subscription.subscriberBlocked === true;
  // SUOL: We should consider making it type-safe by introducing a literal status map + union type, and then replace magic strings in comparisons - this will give us some autocompplete and protect against typos :D
  const isActive = subscription.status === 'Aktiv';
  const isEligibleForDiscount = subscription.providerId === 1 && isActive && !(Number(subscription.productId) in PRODUCT_NAMES_BY_ID);
  const hasDiscount = subscription.discount;
  const isMayflowerAddon = (subscription.externalSubscriptions?.externalSubscriptionResponses?.length ?? 0) > 0;

  const formatProviderId = (providerId: any) => {
    switch (providerId) {
      case 1:
        return 'TV 2';
      case 2:
        return 'TV 2 ansat';
      case 38:
      case 39:
        return 'B2B';
      default:
        return 'Partner';
    }
  };

  const formatProducts = (value: any) => {
    if (!value.features?.CHCHG) {
      return '';
    }
    return value.features.CHCHG.map((feature: any) => {
      const channel = products.find((ch: any) => ch.id === _.toNumber(feature.paramValue));
      return channel?.title;
    })
      .filter((feature: any) => feature)
      .join(', ');
  };

  const formatOrderLines = (value: any) =>
    value
      .map((orderLine: any) => {
        const channel = products.find((ch: any) => ch.id === orderLine.value);
        return channel?.title;
      })
      .filter((orderLine: any) => orderLine)
      .join(', ');

  const formatEvent = (value: any) => {
    const { userEventType } = value;
    if (!userEventType) {
      return '';
    }
    const { displayText, eventSubTypeDescription, eventTypeDescription } = userEventType;
    return displayText || `${eventSubTypeDescription} - ${eventTypeDescription}`;
  };

  type DetailItem = { id: string; label: string; value: any; formatter?: (value: any) => any };

  const getSubscriptionDetails = (): DetailItem[] => [
    {
      id: 'subscriptionId',
      label: t('subscription.subscriptionId'),
      value: subscription.id,
    },
    {
      id: 'subscriptionType',
      label: t('subscription.subscriptionType'),
      value: subscription.type,
    },
    {
      id: 'subscriptionStatus',
      label: t('subscription.subscriptionStatus'),
      value: subscription.status,
    },
    {
      id: 'subscriptionStartTime',
      label: t('subscription.subscriptionStartTime'),
      value: subscription.startTime,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'subscriptionLastModified',
      label: t('subscription.subscriptionLastModified'),
      value: subscription.lastModified,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'subscriptionNextPaymentTime',
      label: t('subscription.subscriptionNextPaymentTime'),
      value: subscription.nextPaymentTime,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'subscriptionExpired',
      label: t('subscription.subscriptionExpired'),
      value: subscription.expireTime,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'subscriptionCommitmentEndTime',
      label: t('subscription.subscriptionCommitmentEndTime'),
      value: subscription.commitmentEndTime,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'subscriptionProductName',
      label: t('subscription.subscriptionProductName'),
      value: subscription.productName,
    },
    {
      id: 'subscriptionAmount',
      label: t('subscription.subscriptionAmount'),
      value: subscription.amount,
      formatter: value => StringUtils.getFormattedSum(value),
    },
    {
      id: 'subscriptionVatAmount',
      label: t('subscription.subscriptionVatAmount'),
      value: subscription.vatAmount,
      formatter: value => StringUtils.getFormattedSum(value),
    },
    {
      id: 'subscriptionDuration',
      label: t('subscription.subscriptionDuration'),
      value: subscription.duration,
      formatter: value => StringUtils.getFormattedDays(value),
    },
    {
      id: 'subscriptionProvider',
      label: t('subscription.subscriptionProvider'),
      value: subscription.providerId,
      formatter: formatProviderId,
    },
    {
      id: 'subscriptionPaymentCount',
      label: t('subscription.subscriptionPaymentCount'),
      value: subscription.paymentCount,
    },
    {
      id: 'subscriptionChannels',
      label: t('subscription.subscriptionChannels'),
      value: subscription.products,
      formatter: formatProducts,
    },
  ];

  let psp: string;
  if (subscription.paymentInfo) {
    switch (subscription.paymentInfo.pspId) {
      case 1:
        psp = 'Nets/DIBS';
        break;
      case 2:
        psp = 'Stripe';
        break;
      case 3:
        psp = 'Frisbii';
        break;
      default:
        psp = 'Ukendt';
    }
  } else {
    psp = '';
  }

  const getSubscriptionPaymentInfo = (): DetailItem[] =>
    subscription.paymentInfo && subscription.type !== 'Support'
      ? [
          {
            id: 'paymentPsp',
            label: t('payment.paymentPsp'),
            value: psp,
          },
          {
            id: 'paymentPspCustomerId',
            label: t('payment.paymentPspCustomerId'),
            value: subscription.paymentInfo.pspCustomerId,
          },
          {
            id: 'paymentCardUpdate',
            label: t('payment.paymentCardUpdate'),
            value: subscription.paymentInfo.cardUpdate,
            formatter: value => DateUtils.getDateTime(value),
          },
          {
            id: 'discountUnlockDate',
            label: t('payment.discountUnlockDate'),
            value: subscription.paymentInfo.discountUnlockDate,
            formatter: value => DateUtils.getDateTime(value),
          },
          {
            id: 'paymentRecurringId',
            label: t('payment.paymentRecurringId'),
            value: subscription.paymentInfo.recurringId,
          },
          {
            id: 'paymentType',
            label: t('payment.paymentType'),
            value: subscription.paymentInfo.paymentType,
          },
          {
            id: 'paymentCardType',
            label: t('payment.paymentCardType'),
            value: subscription.paymentInfo.cardType,
          },
          {
            id: 'paymentCardCountry',
            label: t('payment.paymentCardCountry'),
            value: subscription.paymentInfo.cardCountry,
          },
          {
            id: 'paymentCardExpiration',
            label: t('payment.paymentCardExpiration'),
            value: subscription.paymentInfo,
            formatter: value => `${value.cardExpMonth}/${value.cardExpYear}`,
          },
          {
            id: 'paymentCardNumber',
            label: t('payment.paymentCardNumber'),
            value: subscription.paymentInfo,
            formatter: value =>
              value.cardPrefix && value.cardMask
                ? `${value.cardPrefix}${value.cardMask.slice(value.cardPrefix.length)}`
                : value.cardMask,
          },
          {
            id: 'paymentCardId',
            label: t('payment.paymentCardId'),
            value: subscription.paymentInfo.pic,
          },
        ]
      : [];

  const getSubscriptionPartner = (): DetailItem[] => [
    {
      id: 'providerId',
      label: t('provider.providerId'),
      value: subscription.providerId,
    },
    ...(subscription.provider
      ? ([
          {
            id: 'providerStatus',
            label: t('provider.providerStatus'),
            value: subscription.provider,
            formatter: (value: any) => `${value.name}${value.key ? ` [${value.key}]` : ''}`,
          },
          {
            id: 'providerCreated',
            label: t('provider.providerCreated'),
            value: subscription.provider.created,
            formatter: (value: any) => DateUtils.getDateTime(value),
          },
          {
            id: 'providerUpdated',
            label: t('provider.providerUpdated'),
            value: subscription.provider.last_modified,
            formatter: (value: any) => DateUtils.getDateTime(value),
          },
          {
            id: 'providerRenewalId',
            label: t('provider.providerRenewalId'),
            value: subscription.partnerExtensions?.vasToken,
          },
        ] as DetailItem[])
      : []),
  ];

  const getSubscriptionProductChange = (): DetailItem[] => [
    {
      id: 'productChangeId',
      label: t('product.productChangeId'),
      value: subscription.productchange?.id,
    },
    {
      id: 'productChangeName',
      label: t('product.productChangeName'),
      value: subscription.productchange?.name,
    },
    {
      id: 'productChangeDuration',
      label: t('product.productChangeDuration'),
      value: subscription.productchange?.duration,
      formatter: value => StringUtils.getFormattedDays(value),
    },
    {
      id: 'productChangePrice',
      label: t('product.productChangePrice'),
      value: subscription.productchange?.price,
      formatter: value => StringUtils.getFormattedSum(value),
    },
    {
      id: 'productChangeEffectiveDate',
      label: t('product.productChangeEffectiveDate'),
      value: subscription.productchange?.effective_date,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'productChangeOrderLines',
      label: t('product.productChangeOrderLines'),
      value: subscription.productchange?.orderLines,
      formatter: formatOrderLines,
    },
  ];

  const getSubscriptionDiscount = (): DetailItem[] => [
    {
      id: 'discountId',
      label: t('discount.discountId'),
      value: subscription.discount?.id,
    },
    {
      id: 'discountType',
      label: t('discount.discountType'),
      value: subscription.discount?.discountType,
    },
    {
      id: 'discountValue',
      label: t('discount.discountValue'),
      value: subscription.discount?.discountValue,
      formatter: value => StringUtils.getFormattedDiscount(value, subscription.discount?.discountType),
    },
    {
      id: 'discountCreatedDate',
      label: t('discount.discountCreatedDate'),
      value: subscription.discount?.createdAt,
      formatter: value => DateUtils.getDateTime(value),
    },
    {
      id: 'discountEndDate',
      label: t('discount.discountEndDate'),
      value: subscription.discount?.discountEndAt,
      formatter: value => DateUtils.getDateTime(value),
    },
  ];

  const getSubscriptionEvents = () =>
    _.defaultTo(subscription.events, []).map((event: any) => ({
      id: event.created,
      title: DateUtils.getDateTime(event.created),
      subtitle: event.creator,
      content: <Typography variant="body2">{formatEvent(event)}</Typography>,
    }));

  const handleShowResetCrediCardDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('subscription.subscriptionResetCreditCardConfirm')}</Typography>,
      onConfirm: onResetCreditCard,
    });
  };

  const handleShowSubscriptionChangeDialog = () => {
    showDialog({
      title: t('subscription.subscriptionChange'),
      content: (params: any) => (
        <SubscriptionChange
          subscription={subscription}
          products={products}
          channels={channels}
          onChange={params.setValue}
          onDisable={params.setDisabled}
        />
      ),
      onConfirm: onChangeSubscription,
      confirmButtonText: t('common.change'),
    });
  };

  const handleShowClosePartnerSubscriptionDialog = (vasToken: any) => {
    showDialog({
      title: t('common.important'),
      content: (_params: any) => (
        <>
          <Typography>{t('subscription.subscriptionCloseConfirm')}</Typography>
        </>
      ),
      defaultValue: vasToken,
      onConfirm: onClosePartnerSubscription,
    });
  };

  const handleShowCloseSubscriptionDialog = (forceClose: any) => {
    showDialog({
      title: t('common.important'),
      content: (params: any) => (
        <>
          <Typography>{t('subscription.subscriptionCloseConfirm')}</Typography>
          <FormControlLabel
            className={classes.checkbox}
            control={
              <Checkbox
                id="SubscriptionDetails-forceClose"
                color="primary"
                checked={params.value}
                disabled={forceClose}
                onChange={event => params.setValue(event.target.checked)}
              />
            }
            label={t('subscription.subscriptionCloseForce')}
          />
        </>
      ),
      defaultValue: forceClose,
      onConfirm: onCloseSubscription,
    });
  };

  const handleShowReactivateSubscriptionDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('subscription.subscriptionReactivateConfirm')}</Typography>,
      onConfirm: onReactivateSubscription,
    });
  };

  const handleShowAddonAdminDialog = () => {
    showDialog({
      title: t('subscription.subscriptionAddonAdmin'),
      content: () => (
        <SubscriptionAddonAdmin
          addonSubscriptions={subscription.externalSubscriptions?.externalSubscriptionResponses}
          onResendAddonActivationLink={onResendAddonActivationLink}
          onUnlinkAddonSubscription={onUnlinkAddonSubscription}
        />
      ),
      hideFooter: true,
    });
  };

  const handleRemovePaymentBlock = () => {
    if (isUserBlocked) onRemoveUserBlock();
    if (isCardBlocked) onRemovePaymentBlock(subscription.paymentInfo?.pic ?? '');
  };

  const handleShowDiscountDialog = () => {
    showDialog({
      title: t('subscription.subscriptionAddDiscount'),
      content: (params: any) => (
        <Box className={classes.dialogContent}>
          <SubscriptionDiscountForm
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            onFetchDiscountReasons={onFetchDiscountReasons}
          />
        </Box>
      ),
      onConfirm: onAddSubscriptionDiscount,
      hideFooter: true,
    });
  };

  const renderSubscriptionActions = () => {
    const hasPaymentInfoPic = !_.isEmpty(subscription.paymentInfo?.pic);
    const hasPendingChanges = !_.isEmpty(
      subscription.pendingChanges?.filter(
        (change: any) => change.type === 'CANCELLATION' || change.type === 'PRODUCTCHANGE'
      )
    );
    const hasValidUpgradesOrDowngrades =
      !_.isEmpty(subscription.products?.validUpgrades) ||
      !_.isEmpty(subscription.products?.validDowngrades);

    const canResetCreditCard = hasPaymentInfoPic && !isPartnerAmbassador;
    const canCloseSubscription = !hasPendingChanges;
    const canCancelSubscription =
      !hasPendingChanges &&
      ['Abonnement', 'Tilvalg', 'B2B', 'B2B Voucher', 'B2B Kontrakt'].includes(subscription.type ?? '');
    const canChangeSubscription =
      !hasPendingChanges &&
      hasValidUpgradesOrDowngrades &&
      ['Abonnement'].includes(subscription.type ?? '') &&
      !isPartnerAmbassador;
// SUOL: This seems like some random numbers 👀 We should consider moving them into const with self explanatory names at some point :D Maybe just after this pr!
    if ([1, 2, 38, 39, 66].includes(subscription.providerId ?? -1) && isActive) {
      return (
        <>
          <Button
            id="SubscriptionDetails-resetCreditCard"
            color="primary"
            variant="outlined"
            startIcon={<Icon>credit_card</Icon>}
            onClick={handleShowResetCrediCardDialog}
            disabled={actionsDisabled || !canResetCreditCard}
          >
            {t('subscription.subscriptionResetCreditCard')}
          </Button>
          <Button
            id="SubscriptionDetails-changeSubscription"
            color="primary"
            variant="outlined"
            startIcon={<Icon>swap_horiz</Icon>}
            onClick={handleShowSubscriptionChangeDialog}
            disabled={actionsDisabled || !canChangeSubscription}
          >
            {t('subscription.subscriptionChange')}
          </Button>
          {isMayflowerAddon && (
              <Button
                id="SubscriptionDetails-addonAdminLink"
                color="primary"
                variant="outlined"
                startIcon={<Icon>settings</Icon>}
                onClick={handleShowAddonAdminDialog}
                disabled={actionsDisabled}
              >
                {t('subscription.subscriptionAddonAdmin')}
              </Button>
          )}
          <Button
            id="SubscriptionDetails-closeSubscription"
            color="primary"
            variant="outlined"
            startIcon={<Icon>cancel_presentation</Icon>}
            onClick={() =>
              isPartnerAmbassador
                ? handleShowClosePartnerSubscriptionDialog(subscription.partnerExtensions?.vasToken)
                : handleShowCloseSubscriptionDialog(!canCancelSubscription)
            }
            disabled={actionsDisabled || !canCloseSubscription}
          >
            {t('subscription.subscriptionClose')}
          </Button>
        </>
      );
    }
    if (
      [1, 2].includes(subscription.providerId ?? -1) &&
      subscription.status === 'CANCELLED' &&
      DateUtils.isAfterToday(subscription.nextPaymentTime)
    ) {
      return (
        <Button
          id="SubscriptionDetails-reactivate"
          color="primary"
          variant="outlined"
          startIcon={<Icon>autorenew</Icon>}
          onClick={handleShowReactivateSubscriptionDialog}
          disabled={actionsDisabled}
        >
          {t('subscription.subscriptionReactivate')}
        </Button>
      );
    }
    return null;
  };

  return (
    <Box id="SubscriptionDetails-container" className={classes.container}>
      <Box className={classes.actions}>
        <Button
          id="SubscriptionDetails-back"
          className={classes.back}
          startIcon={<Icon>chevron_left</Icon>}
          onClick={onHideDetails}
          disabled={actionsDisabled}
        >
          {t('common.back')}
        </Button>
        {(isEligibleForDiscount && !hasDiscount) &&
          <Button
            id="SubscriptionList-addDiscount"
            variant="outlined"
            color="primary"
            onClick={handleShowDiscountDialog}
            disabled={actionsDisabled}
            startIcon={<Icon>discount</Icon>}
          >
            {t('subscription.subscriptionAddDiscount')}
          </Button>
        }
        {(isCardBlocked || isUserBlocked) &&
          <Button
            id="SubscriptionList-removePaymentBlock"
            variant="outlined"
            color="primary"
            onClick={handleRemovePaymentBlock}
            disabled={actionsDisabled}
            startIcon={<Icon>payment</Icon>}
          >
            {t('subscription.subscriptionRemovePaymentBlock')}
          </Button>
        }
        {renderSubscriptionActions()}
      </Box>
      <Box className={classes.content}>
        <Box className={classes.wrapper}>
          <DetailsSection
            id="SubscriptionDetails"
            title={t('subscription.subscriptionDetails')}
            className={classes.section}
            data={getSubscriptionDetails()}
          />
        </Box>
        <Box className={classes.wrapper}>
          {[1, 38, 39].includes(subscription.providerId ?? -1) ? (
            <DetailsSection
              id="SubscriptionPaymentInfo"
              title={t('subscription.subscriptionPaymentInfo')}
              className={classes.section}
              data={getSubscriptionPaymentInfo()}
              emptyContent={
                <EmptyState message={t('subscription.subscriptionPaymentEmpty')} center={false} />
              }
            />
          ) : (
            <DetailsSection
              id="SubscriptionPartner"
              title={t('subscription.subscriptionPartner')}
              className={classes.section}
              data={getSubscriptionPartner()}
            />
          )}
          {subscription.productchange && (
            <DetailsSection
              id="SubscriptionProductChange"
              title={t('subscription.subscriptionProductChange')}
              className={clsx(classes.section, classes.productChangeSection)}
              data={getSubscriptionProductChange()}
            />
          )}
          {subscription.discount && (
            <DetailsSection
            id="SubscriptionDiscount"
            title={t('subscription.subscriptionDiscount')}
            className={clsx(classes.section, classes.discountSection)}
            data={getSubscriptionDiscount()}
          />
          )}
        </Box>
        <Box className={classes.wrapper}>
          <ListSection
            id="SubscriptionEvents"
            title={t('subscription.subscriptionEvents')}
            className={clsx(classes.section, classes.eventsSection)}
            data={getSubscriptionEvents()}
            emptyContent={
              <EmptyState message={t('subscription.subscriptionEventsEmpty')} center={false} />
            }
          />
        </Box>
      </Box>
    </Box>
  );
}

export default SubscriptionDetails;
